import {
  SendAdHocNotificationInputSchema,
  type SendAdHocNotificationInput,
} from '@erp/rpc';
import type { EmailSendCategory } from '@erp/shared-types';
import type { NotifyRecipientsValue } from 'src/pages/authenticated/workflows/components/config-renderers/shared/NotifyRecipientsFields';
import {
  CUSTOM_MESSAGE_VALUE,
  type NotifyMessageCategory,
} from './notifyMessageTemplates';

export type SendNotificationSubject = {
  kind: 'draft_sales_order' | 'discount_group';
  id: string;
};

/** Validation failures of the Notify dialog, mapped to copy by the component. */
export type NotifyBlocker =
  | 'no_recipients'
  | 'contacts_need_email'
  | 'no_sender_mailbox'
  | 'no_title'
  | 'no_body';

export interface NotifyFormState {
  value: NotifyRecipientsValue;
  /** The picked template ref/id, or `CUSTOM_MESSAGE_VALUE`. */
  messageValue: string;
  title: string;
  body: string;
}

/** Org email-send category (sender/CC defaults) for the record being notified about. */
export function emailCategoryForSubject(
  subject: SendNotificationSubject | undefined,
): EmailSendCategory {
  if (subject?.kind === 'discount_group') return 'discounts';
  if (subject?.kind === 'draft_sales_order') return 'orders';
  return 'general';
}

/** Template group to preselect for the record; null (Custom) without a record. */
export function messageCategoryForSubject(
  subject: SendNotificationSubject | undefined,
): NotifyMessageCategory | null {
  if (subject?.kind === 'discount_group') return 'discounts';
  if (subject?.kind === 'draft_sales_order') return 'orders';
  return null;
}

/** The template ref when the message is template-driven, else undefined. */
export function templateRefOf(messageValue: string): string | undefined {
  return messageValue === CUSTOM_MESSAGE_VALUE ? undefined : messageValue;
}

/** Contract issue path → the dialog blocker it explains. */
const BLOCKER_BY_PATH: Record<string, NotifyBlocker> = {
  recipientUserIds: 'no_recipients',
  recipientContactIds: 'contacts_need_email',
  senderMailbox: 'no_sender_mailbox',
  title: 'no_title',
  body: 'no_body',
};

/**
 * Runs the form through the `sendAdHoc` contract schema itself (no duplicated
 * rules) and maps the issue paths to blockers the dialog can explain. In
 * template mode title/body are omitted, so only the recipient/mailbox rules
 * can fire.
 */
export function computeNotifyBlockers(
  state: NotifyFormState,
  subject?: SendNotificationSubject,
): NotifyBlocker[] {
  const parsed = SendAdHocNotificationInputSchema.safeParse(
    buildSendNotificationInput(state, subject),
  );
  if (parsed.success) return [];
  const blockers = new Set<NotifyBlocker>();
  for (const issue of parsed.error.issues) {
    const blocker = BLOCKER_BY_PATH[String(issue.path[0])];
    if (blocker) blockers.add(blocker);
  }
  return [...blockers];
}

/**
 * The `sendAdHoc` payload for the current form. A template supplies title,
 * body and the email for every channel; a custom message sends the typed
 * text as a plain notification (with an optional custom email subject).
 */
export function buildSendNotificationInput(
  state: NotifyFormState,
  subject: SendNotificationSubject | undefined,
): SendAdHocNotificationInput {
  const { value, title, body } = state;
  const wantsEmail = value.channels.includes('email');
  const templateRef = templateRefOf(state.messageValue);
  const customSubject = value.emailSubject.trim();
  return {
    recipientUserIds: value.recipientUserIds,
    recipientUserGroupIds: value.recipientUserGroupIds,
    recipientContactIds: value.recipientContactIds,
    channels: value.channels,
    title: templateRef ? undefined : title.trim(),
    body: templateRef ? undefined : body.trim(),
    senderMailbox: wantsEmail ? value.senderMailbox : undefined,
    emailTemplateId: templateRef,
    emailSubject:
      wantsEmail && !templateRef && customSubject ? customSubject : undefined,
    subject,
  };
}
